import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthApiError } from '@/api/client';
import { errorMessage } from '@/lib/errorMap';
import { useAuthStore } from '@/stores/useAuthStore';

interface OrgOption {
  id: string;
  name: string;
  business_number: string | null;
  address: string | null;
}

function passwordStrong(pw: string): string | null {
  if (pw.length < 10) return '비밀번호는 10자 이상이어야 합니다';
  let cls = 0;
  if (/[a-z]/.test(pw)) cls++;
  if (/[A-Z]/.test(pw)) cls++;
  if (/\d/.test(pw)) cls++;
  if (/[^A-Za-z0-9]/.test(pw)) cls++;
  if (cls < 3) return '영문 대/소, 숫자, 특수문자 중 3종 이상 포함하세요';
  return null;
}

const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL ?? 'http://localhost:8001';

export function SignupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 회사 선택 (Phase 7c)
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [orgSearch, setOrgSearch] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const signup = useAuthStore((s) => s.signup);
  const navigate = useNavigate();

  // 회사 목록 로드 — 미인증 엔드포인트
  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch(`${AUTH_API_URL}/v1/organizations/public-list`);
        if (r.ok) {
          const data = await r.json();
          setOrgs(data.items as OrgOption[]);
        }
      } catch {
        // 네트워크 실패 시 빈 리스트 — 사용자에게 안내
      }
    })();
  }, []);

  const filteredOrgs = useMemo(() => {
    const q = orgSearch.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        (o.business_number && o.business_number.toLowerCase().includes(q)) ||
        (o.address && o.address.toLowerCase().includes(q)),
    );
  }, [orgs, orgSearch]);

  const selectedOrg = orgs.find((o) => o.id === selectedOrgId) ?? null;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (password !== passwordConfirm) {
      toast.error('비밀번호 확인이 일치하지 않습니다');
      return;
    }
    const pwIssue = passwordStrong(password);
    if (pwIssue) {
      toast.error(pwIssue);
      return;
    }
    if (!selectedOrgId) {
      toast.error('소속 회사를 선택해주세요');
      return;
    }
    if (!agree) {
      toast.error('약관에 동의해야 가입할 수 있습니다');
      return;
    }

    setSubmitting(true);
    try {
      await signup({
        identifier: { email },
        password,
        name,
        phone,
        organization_id: selectedOrgId,
      });
      toast.success('가입 완료. 회사 관리자의 승인 후 이용 가능합니다.');
      navigate('/', { replace: true });
    } catch (e) {
      if (e instanceof AuthApiError) toast.error(errorMessage(e.code));
      else toast.error('네트워크 오류');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>회원가입</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">이메일</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">이름</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="phone">전화번호</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="010-1234-5678"
                autoComplete="tel"
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="password">비밀번호 (10자, 3종 이상)</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="password2">비밀번호 확인</Label>
              <Input
                id="password2"
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="company">소속 회사</Label>
              <div className="relative">
                <Input
                  id="company"
                  value={selectedOrg ? selectedOrg.name : orgSearch}
                  onChange={(e) => {
                    setOrgSearch(e.target.value);
                    setSelectedOrgId(null);
                    setDropdownOpen(true);
                  }}
                  onFocus={() => setDropdownOpen(true)}
                  placeholder="회사명·법인번호·주소로 검색"
                  autoComplete="organization"
                  required
                />
                {dropdownOpen && (
                  <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-64 overflow-auto rounded-md border bg-popover shadow-md">
                    {filteredOrgs.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground">
                        해당하는 회사가 없습니다. 온토 담당자에게 회사 등록을 요청해주세요.
                      </div>
                    ) : (
                      filteredOrgs.map((o) => (
                        <button
                          type="button"
                          key={o.id}
                          className="flex w-full flex-col items-start gap-0.5 border-b px-3 py-2 text-left hover:bg-accent"
                          onClick={() => {
                            setSelectedOrgId(o.id);
                            setOrgSearch('');
                            setDropdownOpen(false);
                          }}
                        >
                          <div className="text-sm font-medium">{o.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {o.business_number ?? '법인번호 미등록'}
                            {o.address ? ` · ${o.address}` : ''}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              {selectedOrg && (
                <p className="text-xs text-muted-foreground">
                  선택됨: {selectedOrg.name}
                  {' — '}가입 후 회사 관리자의 승인이 필요합니다.
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={agree} onCheckedChange={(v) => setAgree(v === true)} />
              이용약관 및 개인정보처리방침에 동의합니다
            </label>

            <Button type="submit" disabled={submitting} className="mt-2">
              {submitting ? '가입 중...' : '회원가입'}
            </Button>

            <div className="text-center text-sm">
              이미 계정이 있나요?{' '}
              <Link to="/login" className="text-primary hover:underline">
                로그인
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
